import React, { useState, useEffect } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { addStyles } from 'react-mathquill';
import { askGPT } from '../components/OpenAIService';
import { updateExerciseProgress } from '../components/progressManager';
import { appConfig } from '../Config';
import { Star, ChevronLeft, ChevronRight, Send, Crown, Sparkles, Loader2 } from 'lucide-react';
import mathJaxConfig from '../components/mathJaxConfig';

addStyles();
const totalExercises = appConfig.totalExercises;

// Cache pour stocker les exercices par compétence
const exerciseCache = new Map();

const formatLaTeX = (text) => text.replace(/\$(.+?)\$/g, '\\($1\\)');

const TrainingWithGPT = ({ competence, onExerciseComplete, userId }) => {
  const [exercises, setExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [studentTextAnswer, setStudentTextAnswer] = useState('');
  const [feedback, setFeedback] = useState({});
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [exercisePoints, setExercisePoints] = useState(0);
  const [showGlitter, setShowGlitter] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Optimisation du chargement avec cache et chargement progressif
  useEffect(() => {
    let isSubscribed = true;
    
    const fetchExercises = async () => {
      // Vérifie si les exercices sont dans le cache
      if (exerciseCache.has(competence.id)) {
        setExercises(exerciseCache.get(competence.id));
        setIsInitialLoading(false);
        return;
      }

      // Charge progressivement les exercices
      const loadExerciseBatch = async () => {
        const prompt = `
          Génère ${totalExercises} exercices de mathématiques pour : "${competence.title}". 
          Format JSON uniquement : {"exercises": [{"question": "..."}]}.
        `;
        
        try {
          const response = await askGPT(prompt);
          if (!isSubscribed) return;

          const jsonStartIndex = response.indexOf('{');
          const jsonEndIndex = response.lastIndexOf('}') + 1;
          const cleanedResponse = response.slice(jsonStartIndex, jsonEndIndex);
          const parsedResponse = JSON.parse(cleanedResponse);

          if (parsedResponse?.exercises) {
            const newExercises = parsedResponse.exercises;
            exerciseCache.set(competence.id, newExercises); // Met en cache
            setExercises(newExercises);
          } else {
            throw new Error("Format incorrect");
          }
        } catch (error) {
          if (isSubscribed) {
            setErrorMessage("Erreur de chargement. Actualisez la page.");
          }
        } finally {
          if (isSubscribed) {
            setIsInitialLoading(false);
            setIsBackgroundLoading(false);
          }
        }
      };

      setIsBackgroundLoading(true);
      loadExerciseBatch();
    };

    fetchExercises();
    return () => {
      isSubscribed = false;
    };
  }, [competence?.id, competence?.title]);

  const handleExerciseSubmit = async () => {
    if (isCorrecting) return;
    setIsCorrecting(true);
    setErrorMessage('');

    const currentExercise = exercises[currentExerciseIndex];
    const exerciseQuestion = currentExercise?.question || '';
    let earnedPoints = studentTextAnswer.trim() ? 50 : 0;
    earnedPoints = Math.min(earnedPoints, 100);

    try {
      const prompt = `
        Voici l'exercice proposé à l'étudiant: "${exerciseQuestion}". 
        Réponse de l'étudiant : "${studentTextAnswer}". 
        Évalue la réponse de l'étudiant de manière constructive en suivant cette structure :
          1. Identifie les éléments de la réponse qui sont corrects et félicite l'étudiant pour cela. 
          2. Explique avec précision et bienveillance les erreurs ou incompréhensions.
          3. Propose des conseils spécifiques pour améliorer la réponse.
        Limite ta réponse à 150 mots et utilise un langage simple et motivant.
      `;

      const response = await askGPT(prompt);
      const cleanedResponse = response.split(' ').slice(0, 150).join(' ');
      setFeedback(prev => ({ ...prev, [currentExerciseIndex]: formatLaTeX(cleanedResponse) }));

      if (userId) {
        await updateExerciseProgress(userId, competence.id, currentExerciseIndex + 1, earnedPoints);
      }
      
      setExercisePoints(earnedPoints);
      setIsAnswered(true);
      setShowGlitter(earnedPoints === 100);

      if (onExerciseComplete) {
        onExerciseComplete();
      }
    } catch (error) {
      setErrorMessage("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsCorrecting(false);
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setStudentTextAnswer('');
      setFeedback({});
      setShowGlitter(false);
      setErrorMessage('');
      setIsAnswered(false);
      setCurrentExerciseIndex(prev => prev + 1);
    }
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setStudentTextAnswer('');
      setFeedback({});
      setShowGlitter(false);
      setErrorMessage('');
      setIsAnswered(false);
      setCurrentExerciseIndex(prev => prev - 1);
    }
  };

  // Affichage du loader initial uniquement si aucun exercice n'est disponible
  if (isInitialLoading && exercises.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        <p className="mt-4 text-gray-600">Préparation des exercices...</p>
      </div>
    );
  }

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* En-tête */}
        <div className="bg-white p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Star className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Espace d'entraînement</h2>
              {isBackgroundLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-2" />
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">
                Exercice {currentExerciseIndex + 1}/{exercises.length || totalExercises}
              </span>
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                <Crown className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-700">{exercisePoints} points</span>
              </div>
            </div>
          </div>
          
          {/* Barre de progression */}
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${exercisePoints}%` }}
              />
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {exercises.length > 0 ? (
            <>
              {/* Question */}
              <div className="bg-gray-50 rounded-xl p-6 shadow-inner">
                <MathJax>
                  <div className="prose prose-indigo">
                    {formatLaTeX(exercises[currentExerciseIndex]?.question)}
                  </div>
                </MathJax>
              </div>

              {/* Zone de réponse */}
              <div className="space-y-4">
                <textarea
                  value={studentTextAnswer}
                  onChange={(e) => setStudentTextAnswer(e.target.value)}
                  placeholder="Rédigez votre réponse ici..."
                  className="w-full p-4 h-32 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />

                <button
                  onClick={handleExerciseSubmit}
                  disabled={isCorrecting || !studentTextAnswer.trim()}
                  className={`w-full p-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors
                    ${isCorrecting || !studentTextAnswer.trim()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                >
                  {isCorrecting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                  {isCorrecting ? 'Analyse en cours...' : 'Soumettre ma réponse'}
                </button>
              </div>

              {/* Feedback */}
              {feedback[currentExerciseIndex] && (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-6 space-y-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">Feedback de Aladinn</span>
                  </div>
                  <MathJax>
                    <div className="prose max-w-none text-gray-700">
                      {feedback[currentExerciseIndex]}
                    </div>
                  </MathJax>
                  {showGlitter && (
                    <div className="flex items-center justify-center gap-2 text-blue-600 font-medium py-2">
                      <Sparkles className="h-5 w-5" />
                      Excellent travail !
                      <Sparkles className="h-5 w-5" />
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <button
                  onClick={handlePreviousExercise}
                  disabled={currentExerciseIndex === 0}
                  className={`p-2 rounded-lg flex items-center gap-1 transition-colors
                    ${currentExerciseIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                  Précédent
                </button>
                <button
                  onClick={handleNextExercise}
                  disabled={currentExerciseIndex === exercises.length - 1}
                  className={`p-2 rounded-lg flex items-center gap-1 transition-colors
                    ${currentExerciseIndex === exercises.length - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
                >
                  Suivant
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {errorMessage || "Aucun exercice disponible."}
            </div>
          )}
        </div>
      </div>
    </MathJaxContext>
  );
};

export default TrainingWithGPT;